<template>
  <article class="review-collection-card" :class="rootClasses">
    <component :is="rootTag" class="review-collection-card__header" :href="headerHref" @click="emit('order-click')">
      <div class="review-collection-card__info">
        <h3 v-if="title" class="review-collection-card__title">
          {{ title }}
        </h3>
        <p v-if="primaryMeta" class="review-collection-card__meta">
          {{ primaryMeta }}
        </p>
        <p v-if="secondaryMeta" class="review-collection-card__meta">
          {{ secondaryMeta }}
        </p>
      </div>
      <img v-if="coverImage" :src="coverImage" alt="" class="review-collection-card__cover" />
    </component>
    <div class="review-collection-card__rating">
      <ReviewStarRating :size="starSize" :disabled="disabled" @select="emit('rate', $event)" />
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { formatOrderPrimaryMeta, formatOrderSecondaryMeta } from '@/entities/pending-review/lib/orderCardMeta';
import type { PendingReviewOrder } from '@/entities/pending-review/model/types';

import ReviewStarRating from './ReviewStarRating.vue';

const props = withDefaults(
  defineProps<{
    order: PendingReviewOrder;
    disabled?: boolean;
    starSize?: 'm' | 'l';
    horizontal?: boolean;
  }>(),
  {
    starSize: 'l',
    horizontal: false,
  },
);

const emit = defineEmits<{
  rate: [value: number];
  'order-click': [];
}>();

const rootClasses = computed(() => ({ 'review-collection-card--horizontal': props.horizontal }));

const title = computed(() => props.order.experience?.title);
const coverImage = computed(() => props.order.experience?.cover_image);
const orderUrl = computed(() => props.order.url);
const rootTag = computed(() => (orderUrl.value ? 'a' : 'div'));
const headerHref = computed(() => orderUrl.value || undefined);
const primaryMeta = computed(() => formatOrderPrimaryMeta(props.order));
const secondaryMeta = computed(() => formatOrderSecondaryMeta(props.order));
</script>

<style lang="scss" scoped>
.review-collection-card {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: var(--alias-space-12, 12px);
  width: 100%;
  height: 100%;
  padding: var(--alias-space-20, 20px);
  background-color: var(--alias-color-background-subtler);
  border-radius: var(--alias-radius-16, 16px);

  @include adaptivity.mobile {
    align-items: center;
  }
}

.review-collection-card__header {
  display: flex;
  gap: var(--alias-space-20, 20px);
  align-items: flex-start;
  justify-content: space-between;
  color: inherit;
  text-decoration: none;
  cursor: pointer;

  @include adaptivity.mobile {
    flex-direction: column-reverse;
    gap: var(--alias-space-12);
    align-items: center;
  }
}

.review-collection-card__info {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--alias-space-4, 4px);
  min-width: 0;

  @include adaptivity.mobile {
    align-items: center;
    width: 100%;
    text-align: center;
  }
}

.review-collection-card__title {
  display: -webkit-box;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
  color: var(--alias-color-text-default);
  -webkit-box-orient: vertical;

  @include typography.base-400-400-semi;
}

.review-collection-card__meta {
  margin: 0;
  color: var(--alias-color-text-default);

  @include typography.base-300-300-regular;
}

.review-collection-card__cover {
  flex-shrink: 0;
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: var(--alias-radius-8, 8px);
}

.review-collection-card__rating {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: auto;

  @include adaptivity.mobile {
    :deep(.review-star-rating__star) {
      padding-right: 0;
      padding-left: 0;
    }
  }
}

.review-collection-card--horizontal {
  @include adaptivity.mobile {
    align-items: stretch;
  }

  .review-collection-card__header {
    @include adaptivity.mobile {
      flex-direction: row;
      gap: var(--alias-space-20);
      align-items: flex-start;
    }
  }

  .review-collection-card__info {
    @include adaptivity.mobile {
      align-items: flex-start;
      width: auto;
      text-align: left;
    }
  }

  .review-collection-card__rating {
    @include adaptivity.mobile {
      :deep(.review-star-rating__star) {
        padding-right: var(--alias-space-4);
        padding-left: var(--alias-space-4);
      }
    }
  }
}
</style>
